// ============================================================
// FICHIER : boutique/toilesia-cart-images.js
// RÔLE : Afficher l'œuvre personnalisée (image protégée par
//        session) sur les vignettes des articles du panier
//        Shopify — pour CHAQUE ligne qui porte les propriétés
//        Toilesia (creation_id / url_apercu / url_image).
//
// Principe (aucune modification de gabarit du thème requise) :
//   1. lit /cart.js → propriétés de chaque ligne de panier ;
//   2. associe chaque ligne DOM à sa donnée via l'attribut
//      `data-key` des lignes du thème Horizon
//      (<tr data-key="...">, vignette .cart-items__media-image) ;
//   3. charge l'image via /image/download avec les en-têtes exigés
//      par l'API (sécurité client X-Auth-* + session X-Session-Token)
//      → URL blob ;
//   4. pose la bonne vignette sur la bonne ligne : plusieurs
//      articles = plusieurs œuvres, SANS s'écraser mutuellement.
//
// Fonctionne : tiroir panier (page produit) ET page /cart.
// Limite Shopify : le paiement affiche toujours l'image produit
// (aucun script client autorisé dans le checkout).
//
// Chargement : idéalement dans theme.liquid (toutes les pages) ou
// au minimum sur les gabarits « product » et « cart ». Autonome :
// s'appuie sur Toilesia.config s'il est chargé, sinon sur les
// valeurs de repli ci-dessous.
// ⚠️ auth-client.js est REQUIS (au moins pour la session chargée) :
//    l'API refuse toute requête sans les en-têtes de sécurité client
//    X-Auth-* (« 403 Cle API invalide ou manquante. »). S'il n'est pas
//    chargé par le thème, ce module l'injecte lui-même à la demande
//    (voir garantirAuth) — le charger AVANT reste préférable.
// ============================================================

window.ToilesiaCartImages = (function () {
    'use strict';

    // Valeurs de repli si config.js n'est pas chargé sur la page.
    var API_URL = (window.Toilesia && Toilesia.config && Toilesia.config.apiUrl)
        || 'https://api.auclairdestoiles.fr';
    var CLE_SESSION = (window.Toilesia && Toilesia.config && Toilesia.config.cleSession)
        || 'toilesia_session';

    var blobsParChemin = {};  // chemin normalisé → URL blob (cache mémoire)
    var minuterie = 0;        // debounce
    var enCours = false;

    // Script de CE fichier : permet de résoudre l'URL des fichiers voisins
    // (auth-client.js). document.currentScript n'est lisible que pendant
    // l'exécution synchrone du fichier → capturé ici.
    var _scriptCourant = document.currentScript;
    var _authEnCours = null;  // promesse de chargement de auth-client.js

    // ------------------------------------------------------------
    // Lecture de la session (même clé localStorage que le widget)
    // ------------------------------------------------------------
    function jetonSession() {
        try {
            return window.localStorage.getItem(CLE_SESSION) || '';
        } catch (e) {
            return '';
        }
    }

    /**
     * Extrait le chemin « storage/... » d'une valeur de propriété.
     * Accepte : 'storage/uploads/apercu_x.jpg', une URL absolue du
     * widget (config.apiUrl + ...) ou un lien http://storage/...
     */
    function normaliserChemin(url) {
        if (!url) return '';
        var valeur = String(url);
        var match = valeur.match(/(storage\/[A-Za-z0-9_\-./]+\.(?:jpg|jpeg|png|webp|gif))/i);
        if (match) return match[1];
        // Repli : on retire schéma + domaine + slashes de tête
        return valeur
            .replace(/^https?:\/\/[^/]+/i, '')
            .replace(/^\/+/, '');
    }

    // ------------------------------------------------------------
    // Sécurité client (en-têtes X-Auth-* exigés par l'API)
    // ------------------------------------------------------------
    /**
     * Résout l'URL d'un fichier voisin (même dossier que ce script), en
     * CONSERVANT la query « ?v=… » de Shopify (clé de cache du CDN).
     * Même convention que toilesia-produit.js (urlCSS).
     * @param {string} nomFichier ex. 'auth-client.js'
     * @returns {string}
     */
    function urlVoisine(nomFichier) {
        if (_scriptCourant && _scriptCourant.src) {
            var src = _scriptCourant.src;
            var i = src.indexOf('?');
            var chemin = (i === -1) ? src : src.slice(0, i);
            return chemin.replace(/[^/]*$/, nomFichier) + (i === -1 ? '' : src.slice(i));
        }
        return nomFichier;
    }

    /** ToilesiaAuth (auth-client.js) est-il prêt ? */
    function authDisponible() {
        return !!(window.ToilesiaAuth && typeof window.ToilesiaAuth.headersSecurite === 'function');
    }

    /** Balise <script> chargeant déjà auth-client.js (thème), si présente. */
    function scriptAuthExistant() {
        var scripts = document.getElementsByTagName('script');
        for (var i = 0; i < scripts.length; i++) {
            if (/auth-client\.js(\?|$)/.test(scripts[i].src || '')) return scripts[i];
        }
        return null;
    }

    /**
     * Garantit la disponibilité de auth-client.js (ToilesiaAuth) : déjà
     * chargé par le thème (recommandé) ou injecté ici à la demande, une
     * seule fois. Sans lui, impossible de fabriquer les en-têtes X-Auth-* :
     * l'API répondrait 403 « Cle API invalide ou manquante. ».
     * @returns {Promise<boolean>} true si ToilesiaAuth.headersSecurite est là
     */
    function garantirAuth() {
        if (authDisponible()) return Promise.resolve(true);
        if (_authEnCours) return _authEnCours;

        _authEnCours = new Promise(function (resolve) {
            // auth-client.js du thème peut être EN COURS de chargement
            // (async / defer) : on attend alors son exécution SANS le
            // charger une seconde fois (un double chargement re-déclarerait
            // ses constantes et ferait échouer le script).
            if (scriptAuthExistant()) {
                var essais = 0;
                (function attendre() {
                    if (authDisponible()) return resolve(true);
                    if (++essais > 40) { // ~2 s
                        console.error(
                            '[ToilesiaCartImages] auth-client.js chargé mais inutilisable ' +
                            "(ToilesiaAuth.headersSecurite absent) — mise à jour à déployer."
                        );
                        return resolve(false);
                    }
                    setTimeout(attendre, 50);
                })();
                return;
            }

            // Aucune balise connue : on injecte celui du même dossier.
            var script = document.createElement('script');
            script.src = urlVoisine('auth-client.js');
            script.async = true;
            script.onload = function () {
                resolve(authDisponible());
            };
            script.onerror = function () {
                console.error(
                    '[ToilesiaCartImages] auth-client.js introuvable :', script.src,
                    "— l'API refusera les téléchargements d'images (403)."
                );
                resolve(false);
            };
            document.head.appendChild(script);
        });
        return _authEnCours;
    }

    /**
     * En-têtes complets d'un appel protégé :
     *   X-Auth-Token / X-Auth-Date / X-Auth-Salt (auth-client.js)
     * + X-Session-Token (session du client, clé localStorage).
     * ⚠️ index.php exige les en-têtes X-Auth-* sur TOUTES les routes sauf
     *    /ping et /test : un appel qui n'envoie QUE X-Session-Token est
     *    refusé (« 403 Cle API invalide ou manquante. ») et la vignette
     *    reste vide.
     * @returns {Promise<object>}
     */
    function entetesComplets() {
        var jeton = jetonSession();
        var session = jeton ? { 'X-Session-Token': jeton } : {};
        return garantirAuth().then(function (authPrete) {
            if (!authPrete) return session;
            return window.ToilesiaAuth.headersSecurite(session);
        }).catch(function () {
            return session;
        });
    }

    /**
     * Charge l'image protégée (en-têtes X-Auth-* + X-Session-Token) → URL blob.
     * Résultat mis en cache par chemin pour la durée de la page.
     */
    function chargerBlob(chemin) {
        if (!chemin || blobsParChemin[chemin]) {
            return Promise.resolve(blobsParChemin[chemin] || '');
        }
        if (!jetonSession()) return Promise.resolve('');

        return entetesComplets().then(function (entetes) {
            return fetch(API_URL + '/image/download?url=' + encodeURIComponent(chemin), {
                headers: entetes
            });
        }).then(function (reponse) {
            if (!reponse.ok) return null;
            return reponse.blob();
        }).then(function (blob) {
            if (!blob) return '';
            blobsParChemin[chemin] = URL.createObjectURL(blob);
            return blobsParChemin[chemin];
        }).catch(function () {
            return '';
        });
    }

    // ------------------------------------------------------------
    // Association DOM ↔ ligne de panier
    // ------------------------------------------------------------
    /**
     * Informations « œuvre » d'un item de /cart.js.
     * Supporte les nouveaux noms (_creation_id, _url_*, « Votre oeuvre »)
     * ET les anciens (creation_id, url_apercu, url_image) déjà en panier.
     */
    function infosOeuvre(item) {
        var p = (item && item.properties) || {};
        return {
            publique: p['Votre oeuvre'] || p.url_oeuvre || p.url_apercu_public || '',
            chemin: normaliserChemin(
                p._url_apercu || p._url_image || p.url_apercu || p.url_image || ''
            )
        };
    }

    function estLigneToilesia(item) {
        var p = (item && item.properties) || {};
        var o = infosOeuvre(item);
        return !!(o.publique || o.chemin || p._creation_id || p._image_id || p.creation_id);
    }

    /** Toutes les lignes de panier présentes dans le DOM (tiroir + page /cart). */
    function lignesDom() {
        return Array.prototype.slice.call(
            document.querySelectorAll('#cart-drawer tr[data-key], tr[data-key]')
        );
    }

    /**
     * Trouve la ligne DOM correspondant à un item de /cart.js.
     * 1) par data-key (attribut des lignes du thème Horizon) ;
     * 2) repli : par recherche du creation_id dans le texte de la
     *    ligne (le thème affiche les properties).
     */
    function trouverLigne(item, lignes) {
        for (var i = 0; i < lignes.length; i++) {
            if (lignes[i].getAttribute('data-key') === item.key) return lignes[i];
        }
        var cid = String((item.properties && item.properties.creation_id) || '');
        if (cid) {
            for (var j = 0; j < lignes.length; j++) {
                if ((lignes[j].textContent || '').indexOf(cid) !== -1) return lignes[j];
            }
        }
        return null;
    }

    /** Applique le blob / l'URL publique sur la vignette de la ligne (si différente). */
    function appliquerSurLigne(ligne, urlImage) {
        if (!ligne || !urlImage) return false;
        var img = ligne.querySelector(
            '.cart-items__media-image, .cart-items__media img, .cart-item__image, img'
        );
        if (!img) return false;
        if (img.src === urlImage) return true; // déjà appliquée
        img.src = urlImage;
        img.removeAttribute('srcset');
        if (img.style) {
            img.style.opacity = '0';
            img.onload = function () { img.style.opacity = '1'; };
            if (img.complete) img.style.opacity = '1';
        }
        return true;
    }

    /**
     * Masque les propriétés techniques (ids, URL internes) dans le
     * tiroir / la page /cart, sans modification du thème. La propriété
     * publique « Votre oeuvre » reste visible.
     */
    var PROPS_TECHNIQUES = /^(creation_id|image_id|url_image|url_apercu)$/;
    function masquerProprietesTechniques() {
        var divs = document.querySelectorAll('.cart-items__property');
        for (var i = 0; i < divs.length; i++) {
            var dt = divs[i].querySelector('dt');
            if (!dt) continue;
            var nom = ((dt.textContent || '').trim()).replace(/:$/, '');
            if (nom.charAt(0) === '_' || PROPS_TECHNIQUES.test(nom)) {
                divs[i].style.display = 'none';
            }
        }
    }

    // ------------------------------------------------------------
    // Rafraîchissement global
    // ------------------------------------------------------------
    function rafraichir() {
        if (enCours) return Promise.resolve();
        if (!jetonSession()) return Promise.resolve();
        if (!document.querySelector('tr[data-key]')) return Promise.resolve();

        enCours = true;
        return fetch('/cart.js?t=' + Date.now())
            .then(function (reponse) {
                if (!reponse.ok) throw new Error('cart.js indisponible');
                return reponse.json();
            })
            .then(function (panier) {
                var items = ((panier && panier.items) || []).filter(estLigneToilesia);
                if (!items.length) return;

                masquerProprietesTechniques();

                var lignes = lignesDom();
                var promesses = items.map(function (item) {
                    var infos = infosOeuvre(item);
                    var ligne = trouverLigne(item, lignes);
                    if (!ligne) return Promise.resolve(false);
                    // URL publique : affichage direct (pas de session)
                    if (infos.publique) return appliquerSurLigne(ligne, infos.publique);
                    // Repli : image protégée chargée avec la session
                    if (!infos.chemin) return Promise.resolve(false);
                    return chargerBlob(infos.chemin).then(function (blobUrl) {
                        return appliquerSurLigne(ligne, blobUrl);
                    });
                });
                return Promise.all(promesses).then(function () { /* terminé */ });
            })
            .catch(function () { /* silencieux : page sans panier ou hors session */ })
            .then(function () {
                enCours = false;
            });
    }

    function planifier() {
        if (minuterie) clearTimeout(minuterie);
        minuterie = setTimeout(function () {
            minuterie = 0;
            rafraichir();
        }, 250);
    }

    function demarrer() {
        // Au chargement (page /cart notamment)
        rafraichir();

        // Ouverture / fermeture du tiroir panier (événement du thème Horizon)
        document.addEventListener('theme-drawer:open', planifier);
        document.addEventListener('theme-drawer:close', planifier);

        // Re-rendus du panier (ajout, quantité, suppression…) :
        // on surveille les insertions/suppressions de lignes.
        new MutationObserver(planifier).observe(document.body, {
            childList: true,
            subtree: true
        });

        // Vérifications différées pour absorber les re-rendus asynchrones
        [600, 1500].forEach(function (delai) {
            setTimeout(rafraichir, delai);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', demarrer);
    } else {
        demarrer();
    }

    return {
        rafraichir: rafraichir
    };
})();
